// Dependencies
import { useNavigate } from 'react-router-dom';
import { FC, ReactNode, useContext, useState } from 'react';
import { CircularProgress, IconButton, Stack } from '@mui/material';

// Components
import { DoneDialog, PreviewDocDialog, PublishDialog } from '../Dialogs';

// Context
import { AppContext } from '../../context';

// Icons
import { CopyIcon, DeleteIcon, DownloadIcon, EyeIcon, MoreIcon, PencilIcon, PublishIcon } from '../../assets/icons';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
// Constants
import { ROUTES } from '../../contants';

// Apis
import apis from '../../apis';

// Styles
import styles from './styles';

// Interfaces
import { IDocument } from '../../resource';
import { useToastContext } from '../Toast/toastContext';
import {useAlertContext} from '../Alert/alertContext';

interface IAction {
  icon: ReactNode;
  handler: () => any;
  disabled?: boolean;
}

interface IDocActionsProps {
  doc: IDocument;
  direction?: 'vertical' | 'horizontal';
  archived?: boolean;
  backups?: boolean;
  fetch?: () => void;
}

// Export doc actions
export const DocActions: FC<IDocActionsProps> = ({
  doc,
  direction = 'horizontal',
  archived,
  backups,
  fetch,
}) => {
  // States
  const [visibleActions, setVisibleActions] = useState<boolean>(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [visiblePublishDialog, setVisiblePublishDialog] = useState<boolean>(false);
  const [visibleSuccessDialog, setVisibleSuccessDialog] = useState<boolean>(false);
  const [previewPdf, setPreviewPdf] = useState<string | null>(null);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  const [isBackingUp, setIsBackingUp] = useState<boolean>(false);
  const [isRestoringMaster, setIsRestoringMaster] = useState<boolean>(false);

  // Get navigate from hook
  const navigate = useNavigate();

  // Get context
  const { account, fetchDocuments, isAdmin } = useContext(AppContext);
  const { activeToast } = useToastContext();
  const { activeAlert } = useAlertContext();

  // Open publish dialog handler
  const handleOpenPublishDialog = () => {
    setSelectedDocId(doc._id);
    setVisiblePublishDialog(true);
  };

  // Download pdf document
  const handleDownloadDocument = async () => {
    setIsDownloading(true);
    apis.pdf
      .downloadPdf(doc.file_url)
      .then((res) => {
        const fileURL = window.URL.createObjectURL(res);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = doc.name;
        alink.click();
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  // Duplicate
  const handleDuplicate = () => {
    if (account?.id) {
      setIsDuplicating(true);
      apis.pdf
        .duplicateDocument(account.id, doc._id)
        .then(() => {
          fetchDocuments(account?.id);
          activeToast({
            type: 'success',
            text: 'Document duplicated successfully!',
          });
        })
        .finally(() => {
          setIsDuplicating(false);
        });
    }
  };

  const handleCreateBackup = () => {
    if (!account?.id) return;
    setIsBackingUp(true);
    apis.pdf
      .createDocumentBackup(account.id, doc._id)
      .then(() => {
        activeToast({
          type: 'success',
          text: 'Backup created successfully!',
        });
      })
      .catch((err: any) => {
        activeToast({
          type: 'error',
          text: err?.msg || err?.message || 'Failed to create backup',
        });
      })
      .finally(() => {
        setIsBackingUp(false);
      });
  };

  // Hover handler
  const handleToggleActions = () => {
    setVisibleActions(!visibleActions);
  };

  // Remove button click handler
  const handleRemoveClick = () => {
    if (account?.id) {
      setSelectedDocId(doc._id);
      setIsRemoving(true);
      apis.pdf
        .removeDocument(account?.id, doc._id)
        .then(() => {
          fetch && fetch();
          activeToast({
            type: 'success',
            text: 'Document archived successfully!',
          });
        })
        .finally(() => {
          setIsRemoving(false);
        });
    }
  };

  // Edit handler
  const handleEdit = () => {
    navigate(ROUTES.PDF_VIEWER.replace(':id', doc._id));
  };

  const handleOpenPreviewModal: any = () => {
    setPreviewPdf(doc.file_url);
  };

  // Close publish dialog handler
  const handleClosePublishDialog = () => {
    setVisiblePublishDialog(false);
    setSelectedDocId(null);
  };

  // Open success dialog handler
  const handleOpenSuccessDialog = () => {
    setVisibleSuccessDialog(true);
  };

  // Close success dialog handler
  const handleCloseSuccessDialog = () => {
    setVisibleSuccessDialog(false);
  };

  // View document handler
  const handleViewDocument = () => {
    setVisibleSuccessDialog(false);
  };

  // Close preview modal
  const handleClosePreviewModal = () => {
    setPreviewPdf(null);
  };

  const handleRestoreDocument = () => {
    if (account?.id) {
      setSelectedDocId(doc._id);
      setIsRemoving(true);
      apis.pdf
      .restoreDocument(account?.id, doc._id)
      .then(() => {
        fetch && fetch();
        activeToast({
          type: 'success',
          text: 'Document restored successfully!',
        });
      })
      .finally(() => {
        setIsRemoving(false);
      });
    }
  };

  const handleRestoreAsMaster = () => {
    if (!account?.id || !doc.backedFrom) {
      activeToast({
        type: 'error',
        text: 'This backup has no master document reference.',
      });
      return;
    }

    activeAlert(
      {
        type: 'info',
        text: 'Restore this backup as the master factsheet?',
        description:
          'A safety backup of the current master will be created first. If the master was edited since you loaded this list, restore will fail with a conflict.',
      },
      () => {
        if (!account?.id || !doc.backedFrom) return;
        setIsRestoringMaster(true);
        apis.pdf
          .getDocumentFields(doc.backedFrom)
          .then((fields: any) => {
            const masterUpdatedAt = fields?.updatedAt;
            if (!masterUpdatedAt) {
              throw { msg: 'Could not read master document version for restore.' };
            }
            return apis.pdf.restoreBackupAsMaster(account.id, doc._id, masterUpdatedAt);
          })
          .then(() => {
            fetch && fetch();
            activeToast({
              type: 'success',
              text: 'Backup restored as master. A safety backup of the previous master was created.',
            });
          })
          .catch((err: any) => {
            activeToast({
              type: 'error',
              text: err?.msg || err?.message || 'Failed to restore as master',
            });
          })
          .finally(() => {
            setIsRestoringMaster(false);
          });
      }
    );
  };

  const handleDeleteForeverDocument = () => {
    // Fail-closed for customers: always prompt for the protection password (admins skip).
    const needsPassword = !isAdmin;
    activeAlert({
      type: 'info',
      text: "Are you sure to remove this document?",
      description: " Once it's removed you cannot revert it.",
      requirePassword: needsPassword,
    }, (password?: string) => {
      if (account?.id) {
        setSelectedDocId(doc._id);
        setIsRemoving(true);
        apis.pdf
        .removeForeverDocument(account?.id, doc._id, password)
        .then(() => {
          fetch && fetch();
          activeToast({
            type: 'success',
            text: 'Document removed successfully!',
          });
        })
        .catch((err: any) => {
          activeToast({
            type: 'error',
            text: err?.msg || err?.message || 'Failed to remove document',
          });
        })
        .finally(() => {
          setIsRemoving(false);
        });
      }
    });
  };

  // Define actions
  const actions: IAction[] = backups
    ? [
        {
          icon: isRestoringMaster ? (
            <CircularProgress size={28} color="inherit" />
          ) : (
            <SettingsBackupRestoreIcon />
          ),
          handler: handleRestoreAsMaster,
        },
        {
          icon: <DeleteForeverIcon />,
          handler: handleDeleteForeverDocument,
        },
        {
          icon: <EyeIcon />,
          handler: handleOpenPreviewModal,
        },
      ]
    : archived
    ? [
        {
          icon: <RefreshOutlinedIcon />,
          handler: handleRestoreDocument,
        },
        {
          icon: <DeleteForeverIcon />,
          handler: handleDeleteForeverDocument,
        },
        {
          icon: <EyeIcon />,
          handler: handleOpenPreviewModal,
        },
      ]
    : [
        {
          icon: <PublishIcon />,
          handler: handleOpenPublishDialog,
        },
        {
          icon: isDuplicating ? <CircularProgress size={28} color="inherit" /> : <CopyIcon />,
          handler: handleDuplicate,
        },
        {
          icon: isBackingUp ? (
            <CircularProgress size={28} color="inherit" />
          ) : (
            <BackupOutlinedIcon />
          ),
          handler: handleCreateBackup,
        },
        {
          icon: isRemoving ? <CircularProgress size={28} color="inherit" /> : <DeleteIcon />,
          handler: handleRemoveClick,
        },
        {
          icon: isDownloading ? <CircularProgress size={28} color="inherit" /> : <DownloadIcon />,
          handler: handleDownloadDocument,
          disabled: !doc.file_url,
        },
        {
          icon: <PencilIcon />,
          handler: handleEdit,
        },
        {
          icon: <EyeIcon />,
          handler: handleOpenPreviewModal,
        },
      ];

  // Return doc actions
  return (
    <>
      <Stack
        direction={direction === 'horizontal' ? 'row' : 'column'}
        sx={(theme) => styles.container(theme, { direction })}
      >
        <Stack
          direction={direction === 'horizontal' ? 'row' : 'column'}
          spacing={8}
          sx={(theme) =>
            styles.actions(theme, {
              visible: visibleActions,
              direction,
              archived: archived || backups,
            })
          }
        >
          {actions.map(({ icon, handler, disabled }, aIndex) => (
            <IconButton disabled={disabled} key={aIndex} size="medium" sx={styles.button} onClick={handler}>
              {icon}
            </IconButton>
          ))}
        </Stack>
        <IconButton size="medium" sx={styles.button} onClick={handleToggleActions}>
          <MoreIcon />
        </IconButton>
      </Stack>
      {visiblePublishDialog && selectedDocId && (
        <PublishDialog
          open={visiblePublishDialog}
          doc={doc}
          docId={selectedDocId}
          onClose={handleClosePublishDialog}
          onPublish={handleOpenSuccessDialog}
        />
      )}
      {visibleSuccessDialog && (
        <DoneDialog
          open={visibleSuccessDialog}
          title="Well Done"
          description="You have successfully published this document."
          next="View Document"
          onNext={handleViewDocument}
          onClose={handleCloseSuccessDialog}
        />
      )}
      {previewPdf && <PreviewDocDialog open={!!previewPdf} previewPdf={previewPdf} onClose={handleClosePreviewModal} />}
    </>
  );
};
